import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { grey } from '@mui/material/colors';
import Button from '../../../../components/Button/Button';
import Grid from '../../../../components/Grid/Grid';
import Dialog from '../../../../components/Dialog/Dialog';
import DialogTitle from '../../../../components/DialogTitle/DialogTitle';
import DialogContent from '../../../../components/DialogContent/DialogContent';
import useForm from '../../../../hooks/useForm';
import StartTransferFormingForm from './StartTransferFormingForm';
import CircularProgress from '../../../../components/CircularProgress/CircularProgress';
import ProcessStartPreCheckDialog from '../../ProcessStartPreCheckDialog/ProcessStartPreCheckDialog';
import useCancelToken from '../../../../hooks/useCancelToken';
import { formingStartPreCheck } from '../../../../services/transfer-update';

const CreatePilotTransferDialog = ({
  transInfo,
  transForm,
  machineList,
  materialList,
  open,
  onClose,
  isLoading,
  onStartForming,
}) => {
  const { newCancelToken } = useCancelToken();
  const initialForm = {
    bucketNo: transForm.bucketNo || transInfo.bucketNo || '',
    materialName: transInfo.materialName,
    materialId: transForm.formMaterialId || transInfo.formMaterialId || '',
    machineId: transForm.machineId || transInfo.machineId || transInfo.machineId,
    machineName: machineList.find(
      (item) => item.value === (transForm.machineId || transInfo.machineId)
    )?.name,
    transId: transInfo.transId,
  };
  const { formData, setFormData, formErr, setFormErr, resetFormErr } = useForm(initialForm);
  const [isOpenProcessStartPreCheckDialog, setIsOpenProcessStartPreCheckDialog] = useState(false);
  const [processStartPreCheckResultList, setProcessStartPreCheckResultList] = useState([]);
  const [processStartPreCheckResult, setProcessStartPreCheckResult] = useState('');

  const handleCloseStartTransferForming = () => {
    onClose(false);
    resetFormErr();
    setFormData(initialForm);
  };

  const handleFormChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validateForm = () => {
    const { bucketNo, materialId, materialName, machineId } = formData;
    resetFormErr();
    if (!materialId || materialId === '0' || materialId.length === 0) {
      setFormErr((prev) => ({ ...prev, materialId: true }));
      return false;
    }
    // 檢查 bucketNo 是否是 4 或 5 位數字
    if (
      (materialName === 'M3' && !/^\d{4,5}$/.test(bucketNo)) ||
      (materialName !== 'M3' && !/^\d{5}$/.test(bucketNo))
    ) {
      setFormErr((prev) => ({ ...prev, bucketNo: true }));
      return false;
    }
    if (!machineId || machineId === '0' || machineId.length === 0) {
      setFormErr((prev) => ({ ...prev, machineId: true }));
      return false;
    }
    return true;
  };

  const handleStartTransferForming = () => {
    onStartForming({
      bucketNo: formData.bucketNo,
      formMaterialId: formData.materialId,
      machineId: formData.machineId,
      isForceStart: false,
    });
  };

  const onStartTransferFormingClick = async () => {
    if (!validateForm()) return;
    const cancelToken = newCancelToken();
    await formingStartPreCheck({
      data: {
        transId: transInfo.transId,
        formPowderMaterialId: formData.materialId,
        formPowderMaterialBatchNo: formData.bucketNo,
      },
      cancelToken,
      onSuccess: (data) => {
        if (data?.requiresLeaderApproval) {
          setProcessStartPreCheckResultList(
            data.pilotRows.map((item) => ({
              sourceTransId: item.sourceTransId,
              pilotTransId: item.pilotTransIdWithPrefix,
              pilotTransferDate: format(new Date(item.pilotCreateTimeMillis), 'yyyy-MM-dd'),
              pilotQcResult: item.pilotQcPassed,
            }))
          );
          setProcessStartPreCheckResult({
            processStartPreCheckResult: data.decisionCode,
            processStartPreCheckResultMessage: data.decisionSummary,
          });
          setIsOpenProcessStartPreCheckDialog(true);
        } else {
          handleStartTransferForming();
        }
      },
    });
  };

  const handleForceStartTransferForming = () => {
    if (!validateForm()) return;
    setIsOpenProcessStartPreCheckDialog(false);
    onStartForming({
      bucketNo: formData.bucketNo,
      formMaterialId: formData.materialId,
      machineId: formData.machineId,
      isForceStart: true,
    });
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      bucketNo: transForm.bucketNo || transInfo.bucketNo || '',
      materialId: transForm.formMaterialId || transInfo.formMaterialId.toString() || '',
      materialName: materialList.find(
        (item) => item.value === (transForm.formMaterialId || transInfo.formMaterialId)
      )?.name,
      machineId: transForm.machineId || transInfo.machineId,
      machineName: machineList.find(
        (item) => item.value === (transForm.machineId || transInfo.machineId)
      )?.name,
      transId: transInfo.transId,
    }));
  }, [transInfo, transForm]);

  return (
    <>
      <Dialog open={open} onClose={handleCloseStartTransferForming} fullWidth maxWidth="md">
        <DialogTitle>開始壓型</DialogTitle>
        <DialogContent>
          <StartTransferFormingForm
            formData={formData}
            handleFormChange={handleFormChange}
            formErr={formErr}
            machineList={machineList}
            materialList={materialList}
            setFormData={setFormData}
            disabled={isLoading}
          />
        </DialogContent>
        <Grid container gap={1} justifyContent="flex-end" sx={{ px: 2.5, pt: 0, pb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onClose}
            sx={{ fontWeight: 500, bgcolor: grey[400], color: 'white' }}
            disabled={isLoading}
          >
            取消
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onStartTransferFormingClick}
            sx={{ fontWeight: 500 }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress
                sx={{
                  '& .MuiCircularProgress-root': {
                    width: '20px !important',
                    height: '20px !important',
                    color: 'white',
                  },
                }}
              />
            ) : (
              '確認開始'
            )}
          </Button>
        </Grid>
      </Dialog>
      <ProcessStartPreCheckDialog
        isOpen={isOpenProcessStartPreCheckDialog}
        transId={transInfo.transId}
        onClose={() => setIsOpenProcessStartPreCheckDialog(false)}
        onForceStart={handleForceStartTransferForming}
        processTitle="壓型"
        resultRows={processStartPreCheckResultList}
        precheckResult={processStartPreCheckResult}
      />
    </>
  );
};

CreatePilotTransferDialog.propTypes = {
  transInfo: PropTypes.shape({
    transId: PropTypes.string.isRequired,
    elNo: PropTypes.string.isRequired,
    materialName: PropTypes.string.isRequired,
    machineId: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    bucketNo: PropTypes.string.isRequired,
    formMaterialId: PropTypes.string.isRequired,
  }).isRequired,
  transForm: PropTypes.shape({
    bucketNo: PropTypes.string.isRequired,
    formMaterialId: PropTypes.string.isRequired,
    machineId: PropTypes.string.isRequired,
  }).isRequired,
  machineList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  materialList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onStartForming: PropTypes.func.isRequired,
};

export default CreatePilotTransferDialog;
